import './global.scss';
import React, { useContext, useEffect, useState } from 'react';
import {
  AppLayout,
  FourOhFour,
  Link,
  Route,
  Icon,
  Sidebar,
  SidebarItem,
  SidebarPrioritySection,
  SidebarSection,
  Topnav,
  SkipToContent,
  SkipToContentItem,
  OfflineIndicator,
  useResponsiveValue,
  AppLauncherTrigger,
  AppLauncherContent,
  AppLauncher,
  SIDEBAR_BREAKPOINTS,
} from '@monash/portal-react';
import * as Sentry from '@sentry/browser';
import {
  EncumbranceNotification,
  ErrorPageWrapper,
  MenuContext,
  ImpersonationContext,
  UserMenu,
  NotificationBadge,
  MaintenanceContext,
  ChatBotContext,
  GenericError,
  useAppLauncherItems,
  UserInitialsTrigger,
  useUserMenuAvatar,
  useUserMenuProfile,
  UserInitialsContent,
  NotificationContext,
  UserInitials,
} from '@monash/portal-frontend-common';
import PreviewPanel from './components/ui/preview-panel/PreviewPanel';
import Profile from './components/pages/profile/Profile';
import CourseProgress from './components/pages/course-progress/CourseProgress';
import { StudentContext } from './components/providers/StudentProvider';
import PageNotFoundWrapper from './components/ui/page-not-found-wrapper/PageNotFoundWrapper';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import c from './app.module.scss';
import Contact from './components/pages/contact/Contact';
import StudentServices from './components/pages/student-services/StudentServices';
import { HAS_NOTIFICATIONS_SIDEBAR_BREAKPOINTS } from './constants/breakpoints';

const App = () => {
  const { menu, handleMenu, closeMenu, menuRef, getMenuRef } =
    useContext(MenuContext);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const { fullPageError } = useContext(StudentContext);
  const { currentUser } = useContext(ImpersonationContext);
  const { avatar, profileData } = useContext(StudentContext);
  const { maintenanceData } = useContext(MaintenanceContext);
  const { ChatBotButton } = useContext(ChatBotContext);

  const { showNotificationsList } = useContext(NotificationContext);
  const hasNotifications = Boolean(Object.keys(showNotificationsList).length);

  const size = useResponsiveValue(
    hasNotifications
      ? HAS_NOTIFICATIONS_SIDEBAR_BREAKPOINTS
      : SIDEBAR_BREAKPOINTS
  );

  // Nav AX
  const paths = ['/', '/course-progress', '/contact', '/student-support'];
  const [selectedPageIndex, setSelectedPageIndex] = useState(
    paths.indexOf(window.location.pathname)
  );

  // Sidebar - app launcher
  const [appLauncherOpen, setAppLauncherOpen] = useState(false);
  const { items: appLauncherItems } = useAppLauncherItems();

  // Sidebar - user menu
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { avatarSrc, avatarAlt } = useUserMenuAvatar({
    avatarData: avatar,
    getData: false,
  });
  const { name, email, id, pronouns } = useUserMenuProfile({
    profileData,
    getData: false,
  });

  useEffect(() => {
    Sentry.setUser({ id: currentUser?.uid });

    window.addEventListener('offline', () => {
      setIsOffline(true);
    });
    window.addEventListener('online', () => {
      setIsOffline(false);
    });
  }, []);

  const fullPageMaintenanceType = 'SMSPartialMaintenance';
  const maintenance = maintenanceData?.[fullPageMaintenanceType]?.enabled
    ? maintenanceData?.[fullPageMaintenanceType]
    : {};

  return (
    <SentryErrorBoundary fallback={<GenericError />}>
      <ErrorPageWrapper
        error={fullPageError}
        navTitle="Profile"
        userMenu={<UserMenu avatarData={avatar} profileData={profileData} />}
        title="Whoops, something went wrong ..."
        message="It looks like the page you're trying to reach is on a study break. But we're working on getting it back and running as soon as we can. Please try again later."
        inMaintenance={maintenance}
      >
        <SkipToContent>
          <SkipToContentItem text="Top navigation" skipTo="#topNav" />
          <SkipToContentItem text="Sidebar" skipTo="#sidebar" />
          <SkipToContentItem text="Content" skipTo="#contentContainer" />
        </SkipToContent>

        <AppLayout
          topnav={
            <Topnav
              title="Profile"
              onMenu={handleMenu}
              size={size}
              getMenuRef={getMenuRef}
            >
              <div>
                <NotificationBadge />
                {size !== 'XS' && (
                  <>
                    <AppLauncher items={appLauncherItems} />
                    <ChatBotButton />
                    <UserInitials
                      avatarData={avatar}
                      profileData={profileData}
                      appVersion={import.meta.env.VITE_VERSION}
                    />
                  </>
                )}
              </div>
            </Topnav>
          }
          sidebar={
            <Sidebar
              title="Profile"
              ifMenu={menu}
              toggleMenu={handleMenu}
              closeMenu={closeMenu}
              size={size}
              menuRef={menuRef}
              selected={selectedPageIndex}
              onChange={setSelectedPageIndex}
            >
              {size === 'XS' && (
                <SidebarPrioritySection>
                  <AppLauncherTrigger
                    isInSidebar
                    onClick={() => {
                      setAppLauncherOpen(true);
                      handleMenu();
                    }}
                  />
                  <ChatBotButton isInSidebar onClick={handleMenu} />
                  <UserInitialsTrigger
                    isInSidebar
                    onClick={() => {
                      setUserMenuOpen(true);
                      handleMenu();
                    }}
                    avatarSrc={avatarSrc}
                  />
                </SidebarPrioritySection>
              )}
              <SidebarSection>
                <SidebarItem
                  id="tab-home"
                  linkTo="/"
                  text="Profile"
                  icon={<Icon.Home />}
                  onMenu={closeMenu}
                />
                <SidebarItem
                  id="tab-course-progress"
                  linkTo="/course-progress"
                  text="Course progress"
                  icon={<Icon.ListCheck />}
                  onMenu={closeMenu}
                />

                <SidebarItem
                  id="tab-contact"
                  linkTo="/contact"
                  text="Contact"
                  icon={<Icon.Contact />}
                  onMenu={closeMenu}
                />

                <SidebarItem
                  id="tab-student-support"
                  linkTo="/student-support"
                  text="Student Services"
                  icon={<Icon.Support />}
                  onMenu={closeMenu}
                />
              </SidebarSection>
            </Sidebar>
          }
          content={
            <>
              {/* Sidebar controlled menus */}
              {size === 'XS' && (
                <>
                  <AppLauncherContent
                    shown={appLauncherOpen}
                    setShown={setAppLauncherOpen}
                    items={appLauncherItems}
                    triggerRef={{ current: menuRef }} // menuRef is actually an elem not a ref
                  />
                  <UserInitialsContent
                    shown={userMenuOpen}
                    setShown={setUserMenuOpen}
                    triggerRef={{ current: menuRef }}
                    avatarSrc={avatarSrc}
                    avatarAlt={avatarAlt}
                    name={name}
                    email={email}
                    id={id}
                    pronouns={pronouns}
                    appVersion={import.meta.env.VITE_VERSION}
                  />
                </>
              )}

              {currentUser.hasEncumbrances && <EncumbranceNotification />}

              <Route to="/" exact={true}>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <Profile />
                </SentryErrorBoundary>
              </Route>

              <Route to="/course-progress" exact={true}>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <CourseProgress />
                </SentryErrorBoundary>
              </Route>

              <Route to="/contact" exact={true}>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <Contact />
                </SentryErrorBoundary>
              </Route>

              <Route to="/student-support" exact={true}>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <StudentServices />
                </SentryErrorBoundary>
              </Route>

              <Route to="/preview">
                <SentryErrorBoundary fallback={<GenericError />}>
                  <Profile />
                </SentryErrorBoundary>
              </Route>

              <PreviewPanel />

              <FourOhFour>
                <PageNotFoundWrapper
                  setShowSidebar={setShowSidebar}
                  action={<Link to="/">Go to Profile</Link>}
                />
              </FourOhFour>
            </>
          }
          showSidebar={showSidebar}
        />

        {isOffline && <OfflineIndicator className={c.offlineIndicator} />}
      </ErrorPageWrapper>
    </SentryErrorBoundary>
  );
};

export default App;
